import { useState, useCallback, useMemo } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { useApiClient } from '../../utils/apiClient';
import { invalidateDocumentStatsCache } from './useDocumentStats';
import { getBlockPlacementEntries, formatSignDate, textBlockHasValue, blockHasPlacement } from '../../components/documentSigning/utils';
import { DEFAULT_SIGNATURE_WIDTH_PCT, EMBEDDED_TEXT_FONT_SIZE, STANDARD_PAGE_WIDTH_PT } from '../../components/documentSigning/constants';

const dataUrlToUint8Array = async (dataUrl) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
};

const embedSignatureImage = async (pdfDoc, page, signatureImage, placement) => {
  if (!signatureImage || !placement) return;

  let imageBytes;
  if (signatureImage.startsWith('data:')) {
    imageBytes = await dataUrlToUint8Array(signatureImage);
  } else {
    const response = await fetch(signatureImage);
    imageBytes = new Uint8Array(await response.arrayBuffer());
  }

  const image = await pdfDoc.embedPng(imageBytes).catch(async () => {
    return await pdfDoc.embedJpg(imageBytes);
  });

  const pageWidth = page.getWidth();
  const pageHeight = page.getHeight();

  let sigWidth, sigHeight, x, y;

  if (placement.pdfWidth && placement.pdfHeight &&
      typeof placement.pdfX === 'number' && typeof placement.pdfY === 'number') {
    sigWidth = placement.pdfWidth;
    sigHeight = placement.pdfHeight;
    x = placement.pdfX;
    y = placement.pdfY + placement.pdfHeight;
  } else {
    const widthPct = placement.widthPct || DEFAULT_SIGNATURE_WIDTH_PCT;
    sigWidth = pageWidth * widthPct;
    sigHeight = pageWidth * widthPct * 0.086;

    const startX = placement.xPct * pageWidth;
    // placement.yPct is from top, convert to PDF coords (from bottom)
    const pdfY = pageHeight - (placement.yPct * pageHeight);

    x = startX;
    y = pdfY;
  }

  page.drawImage(image, {
    x: x,
    y: y,
    width: sigWidth,
    height: sigHeight,
  });
};

const embedText = async (pdfDoc, page, text, placement, baseFontSize = EMBEDDED_TEXT_FONT_SIZE) => {
  if (!text || !placement) return;

  const pageWidth = page.getWidth();
  const pageHeight = page.getHeight();

  const scaleFactor = pageWidth / STANDARD_PAGE_WIDTH_PT;
  const fontSize = Math.max(10, Math.min(240, baseFontSize * scaleFactor));

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  let startX, startY;

  if (typeof placement.pdfX === 'number' && typeof placement.pdfY === 'number') {
    // pdfX and pdfY represent the click point for the top-left anchor
    startX = placement.pdfX;
    startY = placement.pdfY;
  } else if (typeof placement.xPct === 'number' && typeof placement.yPct === 'number') {
    // xPct and yPct represent the click point, use it as the starting point
    // placement.yPct is now from top (after fix), convert to PDF coords (from bottom)
    startX = placement.xPct * pageWidth;
    startY = pageHeight - (placement.yPct * pageHeight);
  } else {
    return;
  }

  const textX = startX;
  const textY = startY;

  page.drawText(text, {
    x: textX,
    y: textY,
    size: fontSize,
    font: font,
    color: rgb(0, 0, 0),
  });
};

export const useDocumentSigning = (document, pdfUrl, signatureDraft, signaturePosition, textBlocks, isSignatureRequired = false) => {
  const { apiPost } = useApiClient();
  const [submitting, setSubmitting] = useState(false);

  const hasSignatureImage = Boolean(signatureDraft);
  const hasSignaturePlacement = Boolean(signaturePosition);

  const canSubmitDocument = useMemo(() => {
    if (isSignatureRequired && (!hasSignatureImage || !hasSignaturePlacement)) {
      return false;
    }

    if (textBlocks && textBlocks.length > 0) {
      for (const block of textBlocks) {
        if (block.required) {
          if (!textBlockHasValue(block)) {
            return false;
          }

          if (!blockHasPlacement(block)) {
            return false;
          }
        }
      }
    }

    return true;
  }, [hasSignatureImage, hasSignaturePlacement, textBlocks, isSignatureRequired]);

  const handleSubmitDocument = useCallback(async (showToast, onClose) => {
    if (!canSubmitDocument || submitting) {
      if (isSignatureRequired && (!hasSignatureImage || !hasSignaturePlacement)) {
        showToast('Add your signature before submitting.', 'error');
      } else {
        showToast('Please complete all required fields before submitting.', 'error');
      }
      return;
    }

    if (!pdfUrl) {
      showToast('PDF not loaded.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      if (signaturePosition && signatureDraft) {
        const pageIndex = signaturePosition.pageNumber - 1;
        const page = pdfDoc.getPage(pageIndex);
        await embedSignatureImage(pdfDoc, page, signatureDraft, signaturePosition);
      }

      for (const block of textBlocks) {
        const entries = getBlockPlacementEntries(block);
        for (const entry of entries) {
          if (!entry.placement) continue;

          const pageIndex = entry.placement.pageNumber - 1;
          const page = pdfDoc.getPage(pageIndex);

          const textValue = block.captureValuePerPlacement
            ? entry.value
            : block.value;

          if (!textValue) continue;

          const displayText = block.type === 'date'
            ? formatSignDate(textValue)
            : String(textValue);

          await embedText(pdfDoc, page, displayText, entry.placement, EMBEDDED_TEXT_FONT_SIZE);
        }
      }

      const pdfBytes = await pdfDoc.save({
        useObjectStreams: false,
        updateFieldAppearances: false,
      });

      const docsignParams = {
        document_id: String(document.id),
      };
      const qs = new URLSearchParams(docsignParams).toString();

      const submitResponse = await apiPost(`/docsign/sign_doc?${qs}`, pdfBytes, {
        headers: {
          'Content-Type': 'application/pdf',
        },
      });
      const submitResult = await submitResponse.json();

      if (submitResult.status === 'ok' || submitResult.status === 'success') {
        invalidateDocumentStatsCache();
        showToast('Document signed and saved successfully!', 'success');
        onClose(true);
      } else {
        throw new Error(submitResult.error?.message || 'Failed to save signed document');
      }
    } catch (error) {
      showToast(error?.message || 'Failed to sign document.', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmitDocument, submitting, pdfUrl, signaturePosition, signatureDraft, textBlocks, document, apiPost, hasSignatureImage, hasSignaturePlacement, isSignatureRequired]);

  return {
    submitting,
    canSubmitDocument,
    handleSubmitDocument,
  };
};
